'use client';

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { Breadcrumbs, Button, Checkbox, DispensaryLocations, Header, Select, SponsoredBannersProvider } from '@/components';
import MailIcon from '@/components/icons/MailIcon';
import PhoneIcon from '@/components/icons/PhoneIcon';
import ClockIcon from '@/components/icons/ClockIcon';
import FAQSection from '@/components/sections/FAQSection';
import type { Store } from '@/lib/types/stores';
import { CTA_CONTACT_TEAM, CTA_SHOP_NOW, CTA_TRACK_ORDER } from '@/lib/ux-copy';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

// Mirrors the FAQ topic switcher so the two controls behave the same.
const CONTACT_TAB_BASE =
  'flex w-full items-center justify-center rounded-xl border px-4 py-3 text-body-md font-medium transition-colors transform-none active:!scale-100';
const CONTACT_TAB_ACTIVE = 'border-blue-80 !bg-blue-80 text-constant-white';
const CONTACT_TAB_INACTIVE =
  'border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-15 hover:bg-blue-5';
type ContactTabKey = 'inquiries' | 'delivery' | 'partnerships';

type ContactInfoCard = {
  id: string;
  icon: 'mail' | 'phone' | 'clock';
  title: string;
  subtitle: string;
};

function ContactCardIcon({ icon, size = 18 }: { icon: ContactInfoCard['icon']; size?: number }) {
  if (icon === 'mail') return <MailIcon size={size} className="text-blue-80" />;
  if (icon === 'phone') return <PhoneIcon size={size} className="text-blue-80" />;
  return <ClockIcon size={size} className="text-blue-80" />;
}

const CONTACT_TOPIC_VALUES = new Set([
  'online-order-delivery-support',
  'store-specific-inquiry',
  'product-info-availability',
  'returns-refunds',
  'partnerships-media',
  'general',
]);

function ContactSupportQueryHandoff({
  onHandoff,
}: {
  onHandoff: (payload: { ref: string; context?: string; topic?: string }) => void;
}) {
  const searchParams = useSearchParams();
  const appliedRef = useRef(false);

  useEffect(() => {
    if (appliedRef.current) return;
    const ref = searchParams.get('ref')?.trim();
    if (!ref) return;
    appliedRef.current = true;
    onHandoff({
      ref,
      context: searchParams.get('context')?.trim() || undefined,
      topic: searchParams.get('topic')?.trim() || undefined,
    });
  }, [searchParams, onHandoff]);

  return null;
}

export default function ContactPage() {
  const [stores, setStores] = useState<Store[]>([]);
  const [activeContactTab, setActiveContactTab] = useState<ContactTabKey>('inquiries');
  const [contactTopic, setContactTopic] = useState('general');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [keepUpdated, setKeepUpdated] = useState(true);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ ok: boolean; text: string } | null>(null);

  const applySupportHandoff = useCallback(
    (payload: { ref: string; context?: string; topic?: string }) => {
      if (payload.topic && CONTACT_TOPIC_VALUES.has(payload.topic)) {
        setContactTopic(payload.topic);
      }
      setActiveContactTab('delivery');
      const lines = [
        `Reference ID: ${payload.ref}`,
        payload.context ? `Flow: ${payload.context}` : null,
      ].filter(Boolean) as string[];
      const block = lines.join('\n');
      setContactMessage((prev) => {
        const trimmed = prev.trim();
        if (trimmed.includes(`Reference ID: ${payload.ref}`)) return prev;
        const body =
          trimmed.length >= 10 ? trimmed : 'Please describe what happened:';
        return `${block}\n\n${body}`;
      });
    },
    []
  );

  const isEmailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim()),
    [contactEmail]
  );
  const isPhoneValid = useMemo(
    () => contactPhone.replace(/\D/g, '').length >= 10,
    [contactPhone]
  );
  const isFormValid = useMemo(
    () =>
      contactName.trim().length >= 2 &&
      isEmailValid &&
      isPhoneValid &&
      contactMessage.trim().length >= 10 &&
      agreedPrivacy,
    [contactName, isEmailValid, isPhoneValid, contactMessage, agreedPrivacy]
  );
  const activeStoreContact = useMemo(() => {
    const prioritized =
      stores.find((store) => store.email || store.phone) ||
      null;
    return {
      email: prioritized?.email?.trim() || 'hello@dank.ca',
      phone: prioritized?.phone?.trim() || '403-775-888',
      hasConfiguredValues: Boolean(prioritized?.email || prioritized?.phone),
    };
  }, [stores]);

  const contactTabContent = useMemo(
    () =>
      ({
        inquiries: {
          description:
            'Questions about our Calgary dispensaries or same-day delivery? Our team is here to assist with local orders and Alberta mail shipping',
          ctaHref: '/search',
          ctaLabel: CTA_SHOP_NOW,
          cards: [
            {
              id: 'inquiries-mail',
              icon: 'mail',
              title: activeStoreContact.email,
              subtitle: activeStoreContact.hasConfiguredValues
                ? 'For general questions & support'
                : 'Set store email in Admin > Stores',
            },
            {
              id: 'inquiries-phone',
              icon: 'phone',
              title: activeStoreContact.phone,
              subtitle: activeStoreContact.hasConfiguredValues
                ? 'Speak with a Danktender'
                : 'Set store phone in Admin > Stores',
            },
            { id: 'inquiries-hours', icon: 'clock', title: 'Hours vary by location', subtitle: 'Local store times' },
          ],
        },
        delivery: {
          description:
            'Need help with delivery windows, ETA updates, or address coverage? Our delivery support team can help you quickly.',
          ctaHref: '/account?section=orders',
          ctaLabel: CTA_TRACK_ORDER,
          cards: [
            {
              id: 'delivery-mail',
              icon: 'mail',
              title: activeStoreContact.email,
              subtitle: activeStoreContact.hasConfiguredValues
                ? 'Delivery support inbox'
                : 'Set store email in Admin > Stores',
            },
            {
              id: 'delivery-phone',
              icon: 'phone',
              title: activeStoreContact.phone,
              subtitle: activeStoreContact.hasConfiguredValues
                ? 'Live delivery assistance'
                : 'Set store phone in Admin > Stores',
            },
            { id: 'delivery-hours', icon: 'clock', title: 'Hours vary by location', subtitle: 'During service windows' },
          ],
        },
        partnerships: {
          description:
            'Interested in partnership opportunities with Dank Cannabis? Reach out to our team for collaborations and business inquiries.',
          ctaHref: '/contact',
          ctaLabel: CTA_CONTACT_TEAM,
          cards: [
            {
              id: 'partner-mail',
              icon: 'mail',
              title: activeStoreContact.email,
              subtitle: activeStoreContact.hasConfiguredValues
                ? 'Partnership requests'
                : 'Set store email in Admin > Stores',
            },
            {
              id: 'partner-phone',
              icon: 'phone',
              title: activeStoreContact.phone,
              subtitle: activeStoreContact.hasConfiguredValues
                ? 'Business inquiry line'
                : 'Set store phone in Admin > Stores',
            },
            { id: 'partner-hours', icon: 'clock', title: 'Hours vary by location', subtitle: 'Business support hours' },
          ],
        },
      }) as Record<
        ContactTabKey,
        {
          description: string;
          ctaHref: string;
          ctaLabel: string;
          cards: ContactInfoCard[];
        }
      >,
    [activeStoreContact]
  );
  const activeContactContent = contactTabContent[activeContactTab];

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stores`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data?.stores) setStores(data.stores);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <SponsoredBannersProvider>
      <div className="min-h-screen">
        <Header stores={stores} base={API_BASE} />

        <Suspense fallback={null}>
          <ContactSupportQueryHandoff onHandoff={applySupportHandoff} />
        </Suspense>

        <main className="home-page space-y-16 px-4 pb-16 sm:space-y-24 sm:px-8">
          <section className="relative mt-4 overflow-hidden sm:mt-6" aria-labelledby="contact-hero-heading">
            <div className="mx-auto w-full">
              <div className="relative w-full overflow-hidden rounded-2xl bg-brand-100 sm:rounded-2xl">
                <div className="pointer-events-none absolute inset-0 z-0">
                  <Image
                    src="/media/pages/hero-bg-image.webp"
                    alt=""
                    fill
                    priority
                    className="object-cover object-center opacity-[0.05]"
                    sizes="100vw"
                    aria-hidden
                  />
                </div>
                <div className="pointer-events-none absolute inset-0 z-[1] bg-brand-100/10" aria-hidden />

                <div className="relative z-[2] flex flex-col gap-6 p-4 sm:gap-8 sm:p-6 lg:p-8">
                  <div className="min-w-0 flex-1">
                    <Breadcrumbs items={[{ label: 'Contact us' }]} tone="onDark" />

                    <h1
                      id="contact-hero-heading"
                      className="text-h2-mobile font-bold leading-tight text-white lg:text-h2-desktop"
                    >
                      Contact Dank Cannabis
                    </h1>

                    <p className="mt-4 max-w-[52rem] text-body-16-desktop font-normal leading-relaxed text-white/90">
                      Have a question or just want to say high? Our team is here to help with everything from product
                      recommendations to order tracking. Reach out to us using the form below, give us a call, or visit
                      one of our Calgary locations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section aria-labelledby="contact-form-heading">
            <div className="overflow-hidden rounded-2xl bg-brand-100 p-2 sm:rounded-2xl sm:p-3">
              <div className="grid gap-2 lg:grid-cols-2">
                <div className="rounded-2xl bg-brand-100 p-4 text-white sm:p-6">
                  <h2 id="contact-form-heading" className="text-h3-mobile font-bold sm:text-h3-desktop">
                    How Can We Help You?
                  </h2>
                  <p className="mt-3 max-w-[42rem] text-body-md leading-relaxed text-white/85">
                    Please select a topic below so we can route your message to the right team. Whether it&apos;s
                    tracking an order or checking store stock, we&apos;re here to help.
                  </p>

                  <form
                    className="mt-5 space-y-3 sm:mt-6"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!isFormValid) {
                        setAttemptedSubmit(true);
                        return;
                      }

                      setSubmitting(true);
                      setSubmitResult(null);
                      try {
                        const res = await fetch(`${API_BASE}/api/contact-messages`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            channel: activeContactTab,
                            topic: contactTopic,
                            name: contactName,
                            email: contactEmail,
                            phone: contactPhone,
                            message: contactMessage,
                            keepUpdated,
                          }),
                        });
                        const data = await res.json();
                        if (!res.ok || !data?.success) {
                          setSubmitResult({
                            ok: false,
                            text: data?.error || 'Could not send your message. Please try again.',
                          });
                          return;
                        }
                        setSubmitResult({
                          ok: true,
                          text: "Thanks, we have your message. Our team will reply by email.",
                        });
                        setContactName('');
                        setContactPhone('');
                        setContactEmail('');
                        setContactMessage('');
                        setAgreedPrivacy(false);
                        setAttemptedSubmit(false);
                      } catch {
                        setSubmitResult({
                          ok: false,
                          text: 'Could not reach us just now. Please try again in a moment.',
                        });
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Select
                        name="topic"
                        value={contactTopic}
                        onChange={setContactTopic}
                        disableOptionTextScroll
                        placeholder="General inquiry"
                        options={[
                          { value: 'online-order-delivery-support', label: 'Online order & delivery support' },
                          { value: 'store-specific-inquiry', label: 'Store specific inquiry' },
                          { value: 'product-info-availability', label: 'Product info & availability' },
                          { value: 'returns-refunds', label: 'Returns & refunds' },
                          { value: 'partnerships-media', label: 'Partnerships & media' },
                          { value: 'general', label: 'General inquiry' },
                        ]}
                        className="!min-h-[44px] !rounded-xl"
                        id="contact-topic"
                      />

                      <label className="sr-only" htmlFor="contact-name">
                        Full name
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="Full name"
                        className="h-11 w-full rounded-xl border border-neutral-10 bg-white px-4 text-body-md font-medium text-text-primary placeholder:text-text-secondary outline-none ring-blue-60/50 transition focus:ring-2"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="sr-only" htmlFor="contact-phone">
                        Phone number
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        placeholder="Phone number"
                        className="h-11 w-full rounded-xl border border-neutral-10 bg-white px-4 text-body-md font-medium text-text-primary placeholder:text-text-secondary outline-none ring-blue-60/50 transition focus:ring-2"
                      />

                      <label className="sr-only" htmlFor="contact-email">
                        Email address
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        placeholder="Email address"
                        className="h-11 w-full rounded-xl border border-neutral-10 bg-white px-4 text-body-md font-medium text-text-primary placeholder:text-text-secondary outline-none ring-blue-60/50 transition focus:ring-2"
                      />
                    </div>

                    <label className="sr-only" htmlFor="contact-message">
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={7}
                      value={contactMessage}
                      onChange={(e) => setContactMessage(e.target.value)}
                      placeholder="Message"
                      className="w-full rounded-xl border border-neutral-10 bg-white px-4 py-3 text-body-md font-medium text-text-primary placeholder:text-text-secondary outline-none ring-blue-60/50 transition focus:ring-2"
                    />

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                      <div className="space-y-2 text-body-sm font-medium text-white">
                        <label className="group flex cursor-pointer items-start gap-2">
                          <Checkbox
                            name="privacy"
                            checked={agreedPrivacy}
                            onChange={(e) => setAgreedPrivacy(e.target.checked)}
                            className="mt-0.5"
                          />
                          <span className="transition-colors group-hover:text-text-primary">
                            I agree with{' '}
                            <Link
                              href="/privacy-policy"
                              className="underline decoration-yellow-50/80 underline-offset-2"
                            >
                              privacy policy
                            </Link>
                          </span>
                        </label>
                        <label className="group flex cursor-pointer items-start gap-2">
                          <Checkbox
                            name="offers"
                            checked={keepUpdated}
                            onChange={(e) => setKeepUpdated(e.target.checked)}
                            className="mt-0.5"
                          />
                          <span className="transition-colors group-hover:text-text-primary">Keep me up to date on news and exclusive offers</span>
                        </label>
                        {attemptedSubmit && !isFormValid && (
                          <p className="text-body-sm font-medium text-yellow-50">
                            Please complete all required fields and accept the privacy policy.
                          </p>
                        )}
                        {submitResult && (
                          <p
                            role="status"
                            className={`text-body-sm font-medium ${
                              submitResult.ok ? 'text-green-15' : 'text-yellow-50'
                            }`}
                          >
                            {submitResult.text}
                          </p>
                        )}
                      </div>

                      <Button
                        type="submit"
                        variant="solid"
                        color="white"
                        size="md"
                        disabled={!isFormValid || submitting}
                        className="!h-11 !min-w-[118px] !rounded-xl !px-6"
                      >
                        {submitting ? 'Sending…' : 'Submit'}
                      </Button>
                    </div>
                  </form>
                </div>

                <div className="relative min-h-[320px] overflow-hidden rounded-2xl border border-white/30 bg-[#EDEFF7] sm:min-h-[420px] sm:block hidden">
                  <Image
                    src="/media/pages/contact/hero-image.webp"
                    alt="Decorative envelope illustration for contact section"
                    fill
                    className="object-cover object-center"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    priority
                  />
                </div>
              </div>
            </div>
          </section>

          <section aria-labelledby="customer-inquiries-heading">
            <div className="rounded-2xl bg-[#F6F7FB]">
              <div className="grid gap-4 lg:grid-cols-[1fr_1.6fr] lg:gap-6">
                <div>
                  <h2
                    id="customer-inquiries-heading"
                    className="text-h3-mobile font-bold leading-tight text-text-primary sm:text-h3-desktop"
                  >
                    Customer Inquiries Contacts
                  </h2>
                  <p className="mt-3 text-body-md font-medium leading-relaxed text-text-secondary lg:hidden">
                    {activeContactContent.description}
                  </p>

                  {/* Same treatment as the FAQ topic switcher: one bordered
                      control per option, border and fill both change on the
                      active one. Mixing Button variants gave the three tabs
                      different borders and heights. */}
                  <div className="mt-4 grid gap-1 rounded-xl border border-neutral-10 bg-constant-white p-1">
                    {[
                      { id: 'inquiries' as const, label: 'Customer Inquiries' },
                      { id: 'delivery' as const, label: 'Delivery Support' },
                      { id: 'partnerships' as const, label: 'Partnerships' },
                    ].map((tab, index) => {
                      const isActive = activeContactTab === tab.id;
                      return (
                        <Button
                          key={tab.id}
                          raw
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => setActiveContactTab(tab.id)}
                          className={`${CONTACT_TAB_BASE} ${
                            isActive ? CONTACT_TAB_ACTIVE : CONTACT_TAB_INACTIVE
                          }`}
                        >
                          {tab.label}
                        </Button>
                      );
                    })}
                  </div>

                  <div className="mt-3 space-y-2 lg:hidden">
                    {activeContactContent.cards.map((card) => (
                      <div key={card.id} className="rounded-xl border border-neutral-10 bg-constant-white p-3">
                        <div className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-10 bg-blue-5">
                          <ContactCardIcon icon={card.icon} size={16} />
                        </div>
                        <p className="text-body-18 font-bold text-blue-80">{card.title}</p>
                        <p className="mt-1 text-body-sm font-medium text-text-secondary">{card.subtitle}</p>
                      </div>
                    ))}
                  </div>

                  <Button asChild variant="primary" size="md" className="mt-3 !h-10 !w-full !rounded-xl !px-6">
                    <Link href={activeContactContent.ctaHref}>{activeContactContent.ctaLabel}</Link>
                  </Button>
                </div>

                <div className="max-lg:hidden mt-10">
                  <p className="text-body-md font-medium leading-relaxed text-text-secondary">
                    {activeContactContent.description}
                  </p>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {activeContactContent.cards.map((card) => (
                      <div key={card.id} className="rounded-2xl border border-neutral-10 bg-constant-white p-4">
                        <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-neutral-10 bg-blue-5">
                          <ContactCardIcon icon={card.icon} size={18} />
                        </div>
                        <p className="text-body-18 font-bold text-blue-80">{card.title}</p>
                        <p className="mt-1 text-body-sm font-medium text-text-secondary">{card.subtitle}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <DispensaryLocations base={API_BASE} className="!px-0" />

          <div className="[&>section]:!px-0">
            <FAQSection showSponsored={false} />
          </div>

        </main>
      </div>
    </SponsoredBannersProvider>
  );
}
